import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ColumnDef } from '@tanstack/react-table';
import DataTable from '../components/DataTable';
import { getAuthHeaders } from '../lib/api';
import config from '../lib/config';
import { toast } from 'sonner';

const baseValue = 219;

type PoolRow = { poolName: string; count: number; id?: string };
type CognitoRow = { date: string; count: number; total: number };

type UserCountPayload = {
  metrics: { MetricName: string; data: Record<string, number> }[];
  pools: { poolName: string; count: number }[];
};

export default function MetricsPage() {
  const [filter, setFilter] = useState<'All' | 'Month' | 'Year'>('All');

  const { data, isLoading, error } = useQuery({
    queryKey: ['usercount'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch(`${config.BASE_URL}/usercount`, {
        method: 'GET',
        headers,
      });
      if (!res.ok) throw new Error(String(res.status));
      return res.json() as Promise<UserCountPayload>;
    },
  });

  useEffect(() => {
    if (error) toast.error('Could not load metrics.');
  }, [error]);

  const { poolsTable, cognitoStats, chartData } = useMemo(() => {
    if (!data) {
      return {
        poolsTable: [] as PoolRow[],
        cognitoStats: [] as CognitoRow[],
        chartData: [] as { date: string; total: number }[],
      };
    }
    const signupMetric = data.metrics.find((x) => x.MetricName === 'UserSignups');
    const cognitoRaw = signupMetric?.data || {};
    let processed: CognitoRow[] = [];
    let count = baseValue;
    for (const [key, value] of Object.entries(cognitoRaw)) {
      processed.push({ date: key, count: value, total: 0 });
    }
    processed = processed.sort((a, b) => (a.date < b.date ? 1 : -1));
    for (const row of processed) {
      count += row.count;
      row.total = count;
    }
    if (data.pools?.length) {
      const today = new Date().toISOString().split('T')[0];
      const y = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      const yesterday = processed.find((x) => x.date === y);
      const sum = data.pools[0].count + data.pools[1].count;
      processed = processed.filter((x) => x.date !== today);
      processed.push({
        date: today,
        total: sum,
        count: sum - (yesterday ? yesterday.total : 0),
      });
    }
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    const lastYear = new Date();
    lastYear.setFullYear(lastYear.getFullYear() - 1);

    let chartSlice = processed.map((x) => ({
      date: x.date,
      total: x.total + baseValue,
    }));
    if (filter === 'Month') {
      chartSlice = chartSlice.filter((x) => new Date(x.date) > lastMonth);
    } else if (filter === 'Year') {
      chartSlice = chartSlice.filter((x) => new Date(x.date) > lastYear);
    }

    let pools: PoolRow[] =
      data.pools?.map((p, i) => ({
        ...p,
        id: `pool-${i}`,
      })) ?? [];
    if (data.pools && data.pools.length >= 2) {
      pools = [
        ...pools,
        {
          poolName: 'Total',
          count: data.pools[0].count + data.pools[1].count,
          id: 'total',
        },
      ];
    }

    return {
      poolsTable: pools,
      cognitoStats: processed,
      chartData: chartSlice,
    };
  }, [data, filter]);

  const poolCols: ColumnDef<PoolRow, string>[] = [
    { accessorKey: 'poolName', header: 'Pool' },
    { accessorKey: 'count', header: 'Count' },
  ];

  const dailyCols: ColumnDef<CognitoRow, string>[] = [
    { accessorKey: 'date', header: 'Date' },
    { accessorKey: 'count', header: 'Count' },
    { accessorKey: 'total', header: 'Total' },
  ];

  if (isLoading) return <p className="text-ink-muted">Loading metrics…</p>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-ink">Metrics</h1>
        <p className="text-sm text-ink-muted">
          Cognito signups and pool counts from{' '}
          <code className="rounded bg-slate-100 px-1 text-xs">GET /usercount</code>.
        </p>
      </div>
      <section>
        <h2 className="mb-3 text-lg font-medium text-ink">User pools</h2>
        <DataTable data={poolsTable} columns={poolCols} />
      </section>
      <section className="rounded-xl border border-slate-200/80 bg-white p-4 shadow-soft">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-medium text-ink">User count</h2>
          <div className="flex gap-1 rounded-lg border border-slate-200 p-0.5">
            {(['All', 'Month', 'Year'] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setFilter(v)}
                className={`rounded-md px-3 py-1 text-sm font-medium ${
                  filter === v
                    ? 'bg-brand text-white'
                    : 'text-ink-muted hover:bg-slate-50'
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
        <div className="h-[320px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="total"
                stroke="hsl(158 64% 38%)"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>
      <section>
        <h2 className="mb-3 text-lg font-medium text-ink">Daily new users</h2>
        <DataTable data={cognitoStats} columns={dailyCols} />
      </section>
    </div>
  );
}
